import { useEffect } from 'react';

import { WINDOW_EVENTS } from '@/lib/constants/post-message-types';
import { isInIframe } from '@/lib/utils/dom';
import { maybeDeriveTicketUrl } from '@/lib/utils/ticket-env';
import { isDocumentHidden } from '@/lib/utils/visibility';

export const HEARTBEAT_INTERVAL_MS = 60_000;
// An open but abandoned tab must stop keeping paid browser sessions alive.
export const HEARTBEAT_ACTIVE_WINDOW_MS = 15 * 60_000;
// Tab switching or toggling the panel would otherwise send a beat on every flip.
export const HEARTBEAT_MIN_GAP_MS = 30_000;
const HEARTBEAT_HEADERS = { 'content-type': 'application/json' };
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** The heartbeat carries no credentials, so it skips the website-auth proxy, which forwards only
 *  chat, and goes straight to the chat API for every tenant. */
export function siteAccessHeartbeatUrl(tenant: string): string {
  const baseUrl = maybeDeriveTicketUrl({
    baseUrl: process.env.NEXT_PUBLIC_CHAT_API_BASE_URL ?? '',
    host: typeof window === 'undefined' ? undefined : window.location.host,
  });
  return `${baseUrl}/${tenant}/heartbeat`;
}

/** Pings `url` every minute while the tab is visible and someone used it in the last 15 minutes.
 *  Opening the page counts as use, so the first visitor starts the warm-up. Inside the embed it
 *  also pauses while the panel is closed. No `url`, no pings. */
export function useSiteAccessHeartbeat(url: string | undefined): void {
  useEffect(() => {
    if (!url) return;
    const target = url;
    let lastActivity = Date.now();
    let lastBeatAt = -Infinity;
    // The embed loads the frame on first open, so it starts open.
    let panelOpen = true;
    // The host page's own window gets these events too; only the frame's panel is ours.
    const inEmbed = isInIframe();

    const isActive = () => Date.now() - lastActivity <= HEARTBEAT_ACTIVE_WINDOW_MS;

    function beat() {
      if (isDocumentHidden() || !panelOpen || !isActive()) return;
      lastBeatAt = Date.now();
      // Fire-and-forget: a lost beat only lets the warm sessions idle out a little sooner.
      // Mastra refuses any POST body that isn't JSON (415), so a bare POST never arrives.
      fetch(target, { method: 'POST', headers: HEARTBEAT_HEADERS, body: '{}' }).catch(() => {});
    }

    function beatUnlessRecent() {
      if (Date.now() - lastBeatAt >= HEARTBEAT_MIN_GAP_MS) beat();
    }

    function onActivity() {
      const wasIdle = !isActive();
      lastActivity = Date.now();
      // Someone back after the window lapsed should not wait up to a minute for the warm-up.
      if (wasIdle) beatUnlessRecent();
    }

    // Opening the panel is use, like opening the page.
    function onPanelOpened() {
      panelOpen = true;
      lastActivity = Date.now();
      beatUnlessRecent();
    }

    function onPanelClosed() {
      panelOpen = false;
    }

    beat();
    const timer = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { capture: true, passive: true });
    }
    document.addEventListener('visibilitychange', beatUnlessRecent);
    if (inEmbed) {
      window.addEventListener(WINDOW_EVENTS.OPENED, onPanelOpened);
      window.addEventListener(WINDOW_EVENTS.CLOSED, onPanelClosed);
    }
    return () => {
      clearInterval(timer);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', beatUnlessRecent);
      if (inEmbed) {
        window.removeEventListener(WINDOW_EVENTS.OPENED, onPanelOpened);
        window.removeEventListener(WINDOW_EVENTS.CLOSED, onPanelClosed);
      }
    };
  }, [url]);
}
